(function () {
  "use strict";

  const STORAGE_KEY = "vs_compare_items";
  const CONFIG_CACHE_KEY = "vs_compare_config";
  const CONFIG_CACHE_TTL = 30 * 1000; // 30 sec

  const ICONS = {
    compare:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
    close: "×",
    empty:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
  };

  class CompareWidget {
    constructor(root) {
      this.root = root;
      this.shop = root.dataset.shop;
      this.appUrl = root.dataset.appUrl || "";
      this.btnColor = root.dataset.btnColor || "#1a1a1a";
      this.btnTextColor = root.dataset.btnTextColor || "#ffffff";
      this.barPosition = root.dataset.barPosition || "bottom";
      this.showOnCollection = root.dataset.showOnCollection === "true";
      this.config = null;
      this.items = this.loadItems();

      this.setCSSVars();
      this.init();
    }

    setCSSVars() {
      document.documentElement.style.setProperty(
        "--vs-compare-btn-color",
        this.btnColor,
      );
      document.documentElement.style.setProperty(
        "--vs-compare-btn-text",
        this.btnTextColor,
      );
    }

    async init() {
      await this.fetchConfig();
      if (!this.config || !this.config.enabled) return;

      // Trim items if maxItems was reduced
      const max = this.config.maxItems || 4;
      if (this.items.length > max) {
        this.items = this.items.slice(0, max);
        this.saveItems();
      }

      // Load per-card product_type map injected by Liquid (collection/search pages)
      this.cardTypes = {};
      const typeEl = document.getElementById("vs-compare-card-types");
      if (typeEl) {
        try {
          this.cardTypes = JSON.parse(typeEl.textContent) || {};
        } catch {
          /* ignore */
        }
      }

      this.createBar();
      this.createOverlay();
      await this.injectProductPageButton();
      if (this.showOnCollection) this.injectCollectionButtons();
      this.updateBar();
    }

    isEligibleType(type) {
      const allowed = this.config?.eligibleProductTypes || [];
      if (!allowed.length) return true; // empty = allow all
      return allowed.includes(type);
    }

    // ── Storage ──
    loadItems() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      } catch {
        return [];
      }
    }

    saveItems() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    }

    addItem(product) {
      if (!product || !product.handle) return false;
      if (this.items.find((i) => i.handle === product.handle)) return false;
      if (this.items.length >= (this.config?.maxItems || 4)) return false;
      this.items.push(product);
      this.saveItems();
      this.updateBar();
      this.updateButtons();
      return true;
    }

    removeItem(handle) {
      this.items = this.items.filter((i) => i.handle !== handle);
      this.saveItems();
      this.updateBar();
      this.updateButtons();
    }

    clearItems() {
      this.items = [];
      this.saveItems();
      this.updateBar();
      this.updateButtons();
    }

    isInCompare(handle) {
      return this.items.some((i) => i.handle === handle);
    }

    // ── Config ──
    async fetchConfig() {
      // Check cache
      try {
        const cached = JSON.parse(
          localStorage.getItem(CONFIG_CACHE_KEY) || "null",
        );
        if (cached && Date.now() - cached.ts < CONFIG_CACHE_TTL) {
          this.config = cached.data;
          return;
        }
      } catch {
        /* ignore */
      }

      if (!this.appUrl || !this.shop) return;

      try {
        const sep = this.appUrl.includes("?") ? "&" : "?";
        const res = await fetch(
          `${this.appUrl}/api/compare/config${sep}shop=${encodeURIComponent(this.shop)}`,
        );
        if (!res.ok) return;
        this.config = await res.json();
        localStorage.setItem(
          CONFIG_CACHE_KEY,
          JSON.stringify({ data: this.config, ts: Date.now() }),
        );
      } catch {
        /* silently fail */
      }
    }

    // ── Product Page Button ──
    async injectProductPageButton() {
      let product = null;

      // Try embedded JSON first (from Liquid extension block)
      const dataEl = document.getElementById("vs-compare-product-data");
      if (dataEl) {
        try {
          product = JSON.parse(dataEl.textContent);
        } catch {
          /* ignore */
        }
      }

      // Fallback: detect product page from URL and fetch via AJAX API
      if (!product) {
        const match = window.location.pathname.match(/\/products\/([^?#/]+)/);
        if (!match) return;
        try {
          const res = await fetch(`/products/${match[1]}.json`);
          if (!res.ok) return;
          const { product: p } = await res.json();
          product = {
            id: p.id,
            handle: p.handle,
            title: p.title,
            url: `/products/${p.handle}`,
            image: p.images?.[0]?.src
              ? p.images[0].src.replace(/(\.\w+)$/, "_200x200$1")
              : null,
            price: p.variants?.[0]?.price
              ? this.formatMoney(p.variants[0].price)
              : null,
            price_raw: p.variants?.[0]?.price
              ? parseFloat(p.variants[0].price) * 100
              : 0,
            compare_at_price: p.variants?.[0]?.compare_at_price
              ? this.formatMoney(p.variants[0].compare_at_price)
              : null,
            vendor: p.vendor,
            product_type: p.product_type,
            description:
              p.body_html?.replace(/<[^>]*>/g, "").slice(0, 300) || "",
            weight:
              p.variants?.[0]?.weight > 0
                ? `${p.variants[0].weight}${p.variants[0].weight_unit || "g"}`
                : null,
            sku: p.variants?.[0]?.sku || "",
            availability: p.variants?.some((v) => v.available) ?? false,
            tags: p.tags || "",
            variant_id: p.variants?.[0]?.id || null,
            variants_count: p.variants?.length || 1,
          };
        } catch {
          return;
        }
      }

      this.currentProduct = product;

      // Skip if product_type not eligible
      if (!this.isEligibleType(product.product_type)) return;

      // Find a good place to inject — after add-to-cart or product form
      const targets = [
        'form[action="/cart/add"] button[type="submit"]',
        'form[action="/cart/add"]',
        ".product-form__submit",
        ".product-form__buttons",
        ".product-form",
        ".product__info-wrapper",
        "[data-product-form]",
        ".product-single__meta",
      ];

      let anchor = null;
      for (const sel of targets) {
        anchor = document.querySelector(sel);
        if (anchor) break;
      }

      const btn = this.createButton(product.handle, "full");
      btn.style.marginTop = "10px";

      if (anchor) {
        if (anchor.tagName === "BUTTON") {
          anchor.parentElement.insertAdjacentElement("afterend", btn);
        } else {
          anchor.insertAdjacentElement("afterend", btn);
        }
      } else {
        // Fallback: prepend to body after root
        this.root.insertAdjacentElement("afterend", btn);
      }
    }

    // ── Collection Page Buttons ──
    injectCollectionButtons() {
      // Skip cart page entirely — compare button doesn't belong on cart line items
      // (it overlaps the X remove button and confuses users)
      if (document.body && document.body.classList.contains('template-cart')) return;

      const allLinks = document.querySelectorAll(
        'a[href*="/products/"]:not(.vs-compare-processed)',
      );
      const seen = new Set();

      allLinks.forEach((link) => {
        // Skip nav / header / footer / mega-menu / breadcrumb / cart contexts
        if (
          link.closest(
            "nav, header, footer, [class*='mega-menu'], [class*='navbar'], [class*='menu-item'], [class*='breadcrumb'], [class*='cart__'], [class*='cart-item'], [class*='cart-drawer'], [class*='mini-cart'], #cart, main.cart",
          )
        )
          return;

        const href = link.getAttribute("href");
        const match = href?.match(/\/products\/([^?#/]+)/);
        if (!match) return;

        const handle = match[1];
        if (seen.has(handle)) return;
        seen.add(handle);

        // Filter by product_type if map exists (Liquid injected) — skip non-eligible
        if (Object.keys(this.cardTypes).length > 0) {
          if (!this.isEligibleType(this.cardTypes[handle])) return;
        }

        // Find the closest product card container — prefer image-wrapper link's parent
        // so the mini button sits on the image corner
        const imageAnchor = link.querySelector("img") ? link : null;
        const card =
          (imageAnchor && imageAnchor.parentElement) ||
          link.closest(".product-card") ||
          link.closest(".card") ||
          link.closest('[class*="product-card"]') ||
          link.closest(
            '[class*="product"]:not([class*="mega"]):not([class*="menu"])',
          ) ||
          link.closest("li") ||
          link.parentElement;

        if (!card || card.querySelector(".vs-compare-card-btn")) return;

        const cs = getComputedStyle(card);
        if (cs.position === "static") card.style.position = "relative";

        const btn = this.createButton(handle, "mini");
        card.appendChild(btn);
        link.classList.add("vs-compare-processed");
      });
    }

    // ── Button Factory ──
    createButton(handle, type) {
      const btn = document.createElement("button");
      const isActive = this.isInCompare(handle);

      if (type === "mini") {
        btn.className = `vs-compare-card-btn${isActive ? " vs-active" : ""}`;
        btn.innerHTML = isActive ? ICONS.check : ICONS.compare;
        btn.title = isActive ? "ลบออก" : "เปรียบเทียบ";
      } else {
        btn.className = `vs-compare-btn${isActive ? " vs-active" : ""}`;
        btn.innerHTML = `${isActive ? ICONS.check : ICONS.compare}<span>${isActive ? "เพิ่มแล้ว" : "เปรียบเทียบ"}</span>`;
      }

      btn.dataset.vsCompareHandle = handle;
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleProduct(handle);
      });

      return btn;
    }

    async toggleProduct(handle) {
      if (this.isInCompare(handle)) {
        this.removeItem(handle);
        return;
      }

      // Check max
      if (this.items.length >= (this.config?.maxItems || 4)) {
        this.showToast(
          `เปรียบเทียบได้สูงสุด ${this.config?.maxItems || 4} ชิ้น`,
        );
        return;
      }

      // If we have current product data (on product page)
      if (this.currentProduct && this.currentProduct.handle === handle) {
        this.addItem(this.currentProduct);
        return;
      }

      // Fetch product data from AJAX API
      try {
        const res = await fetch(`/products/${handle}.json`);
        if (!res.ok) return;
        const { product } = await res.json();
        this.addItem({
          id: product.id,
          handle: product.handle,
          title: product.title,
          url: `/products/${product.handle}`,
          image: product.images?.[0]?.src
            ? product.images[0].src.replace(/(\.\w+)$/, "_200x200$1")
            : null,
          price: product.variants?.[0]?.price
            ? this.formatMoney(product.variants[0].price)
            : null,
          price_raw: product.variants?.[0]?.price
            ? parseFloat(product.variants[0].price) * 100
            : 0,
          compare_at_price: product.variants?.[0]?.compare_at_price
            ? this.formatMoney(product.variants[0].compare_at_price)
            : null,
          vendor: product.vendor,
          product_type: product.product_type,
          description:
            product.body_html?.replace(/<[^>]*>/g, "").slice(0, 300) || "",
          weight:
            product.variants?.[0]?.weight > 0
              ? `${product.variants[0].weight}${product.variants[0].weight_unit || "g"}`
              : null,
          sku: product.variants?.[0]?.sku || "",
          availability: product.variants?.some((v) => v.available) ?? false,
          tags: product.tags || "",
          variant_id: product.variants?.[0]?.id || null,
          variants_count: product.variants?.length || 1,
        });
      } catch {
        /* silently fail */
      }
    }

    formatMoney(amount) {
      const num = parseFloat(amount);
      if (isNaN(num)) return amount || "";
      // Use Shopify's currency if available, otherwise default to ฿
      const currency = Shopify?.currency?.active || "THB";
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(num);
      } catch {
        return `฿${num.toFixed(2)}`;
      }
    }

    // ── Update All Buttons ──
    updateButtons() {
      document.querySelectorAll("[data-vs-compare-handle]").forEach((btn) => {
        const handle = btn.dataset.vsCompareHandle;
        const isActive = this.isInCompare(handle);
        const isMini = btn.classList.contains("vs-compare-card-btn");

        btn.classList.toggle("vs-active", isActive);

        if (isMini) {
          btn.innerHTML = isActive ? ICONS.check : ICONS.compare;
          btn.title = isActive ? "ลบออก" : "เปรียบเทียบ";
        } else {
          btn.innerHTML = `${isActive ? ICONS.check : ICONS.compare}<span>${isActive ? "เพิ่มแล้ว" : "เปรียบเทียบ"}</span>`;
        }
      });
    }

    // ── Floating Bar ──
    createBar() {
      const bar = document.createElement("div");
      bar.className = `vs-compare-bar vs-bar-${this.barPosition} vs-hidden`;
      bar.id = "vs-compare-bar";
      bar.innerHTML = `
        <div class="vs-compare-bar-items" id="vs-compare-bar-items"></div>
        <div class="vs-compare-bar-actions">
          <span class="vs-compare-bar-count" id="vs-compare-bar-count"></span>
          <button class="vs-compare-bar-clear" id="vs-compare-bar-clear">ล้างทั้งหมด</button>
          <button class="vs-compare-bar-btn vs-primary" id="vs-compare-bar-compare">เปรียบเทียบเลย</button>
        </div>
      `;
      document.body.appendChild(bar);

      document
        .getElementById("vs-compare-bar-clear")
        .addEventListener("click", () => this.clearItems());
      document
        .getElementById("vs-compare-bar-compare")
        .addEventListener("click", () => this.openDrawer());

      this.bar = bar;
    }

    updateBar() {
      if (!this.bar) return;
      const container = document.getElementById("vs-compare-bar-items");
      const count = document.getElementById("vs-compare-bar-count");
      const max = this.config?.maxItems || 4;

      if (this.items.length === 0) {
        this.bar.classList.add("vs-hidden");
        return;
      }
      this.bar.classList.remove("vs-hidden");

      const itemSlots = this.items.map(
        (item) => `
        <div class="vs-compare-bar-item">
          ${item.image ? `<img src="${this.escAttr(item.image)}" alt="${this.esc(item.title)}">` : ""}
          <span class="vs-compare-bar-item-title">${this.esc(item.title)}</span>
          <button class="vs-compare-bar-item-remove" data-handle="${this.escAttr(item.handle)}">${ICONS.close}</button>
        </div>
      `,
      );

      const emptySlots = Array.from({ length: max - this.items.length }).map(
        () => `<div class="vs-compare-bar-slot">+</div>`,
      );

      container.innerHTML = [...itemSlots, ...emptySlots].join("");

      count.textContent = `${this.items.length}/${max} สินค้า`;

      container
        .querySelectorAll(".vs-compare-bar-item-remove")
        .forEach((btn) => {
          btn.addEventListener("click", () =>
            this.removeItem(btn.dataset.handle),
          );
        });
    }

    // ── Compare Drawer / Overlay ──
    createOverlay() {
      const overlay = document.createElement("div");
      overlay.className = "vs-compare-overlay";
      overlay.id = "vs-compare-overlay";
      overlay.innerHTML = `
        <div class="vs-compare-drawer">
          <div class="vs-compare-drawer-header">
            <h2>เปรียบเทียบสินค้า</h2>
            <button class="vs-compare-drawer-close" id="vs-compare-drawer-close">${ICONS.close}</button>
          </div>
          <div class="vs-compare-drawer-body" id="vs-compare-drawer-body"></div>
        </div>
      `;
      document.body.appendChild(overlay);

      // Close on X button
      document
        .getElementById("vs-compare-drawer-close")
        .addEventListener("click", () => this.closeDrawer());

      // Close on backdrop click
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closeDrawer();
      });

      // Close on Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.closeDrawer();
      });

      this.overlay = overlay;
    }

    async openDrawer() {
      if (this.items.length < 2) {
        this.showToast("เพิ่มสินค้าอย่างน้อย 2 ชิ้นเพื่อเปรียบเทียบ");
        return;
      }

      // Show drawer immediately with loading state
      this.overlay.classList.add("vs-open");
      document.body.style.overflow = "hidden";
      const body = document.getElementById("vs-compare-drawer-body");
      body.innerHTML =
        '<div class="vs-compare-loading"><div class="vs-compare-spinner"></div><p>กำลังโหลดข้อมูล...</p></div>';

      // Fetch metafields then render
      await this.enrichWithMetafields();
      this.renderCompareTable();
    }

    async enrichWithMetafields() {
      if (!this.appUrl || !this.shop || this.items.length === 0) return;
      const handles = this.items.map((i) => i.handle).join(",");
      try {
        const sep = this.appUrl.includes("?") ? "&" : "?";
        const res = await fetch(
          `${this.appUrl}/api/product-metafields${sep}shop=${encodeURIComponent(this.shop)}&handles=${encodeURIComponent(handles)}`,
        );
        if (!res.ok) return;
        const data = await res.json();
        // Merge metafields into stored items
        this.items = this.items.map((item) => ({
          ...item,
          metafields: {
            ...(item.metafields || {}),
            ...(data[item.handle] || {}),
          },
        }));
        this.saveItems();
      } catch {
        /* silent fail — compare works without metafields */
      }
    }

    closeDrawer() {
      this.overlay.classList.remove("vs-open");
      document.body.style.overflow = "";
    }

    renderCompareTable() {
      const body = document.getElementById("vs-compare-drawer-body");
      const fields = this.config?.fields || [];

      if (this.items.length === 0) {
        body.innerHTML = `
          <div class="vs-compare-empty">
            ${ICONS.empty}
            <p>ยังไม่มีสินค้าให้เปรียบเทียบ</p>
          </div>
        `;
        return;
      }

      const fieldMap = {
        price: (p) => p.price || "—",
        compare_at_price: (p) => p.compare_at_price || "—",
        vendor: (p) => p.vendor || "—",
        product_type: (p) => p.product_type || "—",
        description: (p) => p.description || "—",
        weight: (p) => p.weight || "—",
        sku: (p) => p.sku || "—",
        availability: (p) =>
          p.availability
            ? '<span class="vs-in-stock">มีสินค้า</span>'
            : '<span class="vs-out-of-stock">สินค้าหมด</span>',
        tags: (p) => p.tags || "—",
      };

      // Build table
      let html = '<table class="vs-compare-table">';

      // Header row — product images & names
      html += "<thead><tr><th></th>";
      this.items.forEach((item) => {
        html += `<th>
          <div class="vs-compare-product-header">
            ${item.image ? `<img src="${this.escAttr(item.image)}" alt="${this.esc(item.title)}">` : ""}
            <a class="vs-product-name" href="${this.safeUrl(item.url)}">${this.esc(item.title)}</a>
            <span class="vs-product-price">${this.esc(item.price || "")}</span>
            <button class="vs-product-remove" data-handle="${this.escAttr(item.handle)}">ลบออก</button>
          </div>
        </th>`;
      });
      html += "</tr></thead><tbody>";

      // Field rows
      fields.forEach((field) => {
        html += `<tr><td>${this.esc(field.label)}</td>`;
        this.items.forEach((item) => {
          const getter = fieldMap[field.key];
          let value;
          if (getter) {
            value = getter(item);
          } else if (
            field.type === "custom" &&
            item.metafields &&
            item.metafields[field.key] != null
          ) {
            value = this.renderMetafield(item.metafields[field.key]);
          } else {
            value = "—";
          }
          html += `<td>${value}</td>`;
        });
        html += "</tr>";
      });

      // CTA row — single button "หยิบใส่ตะกร้า" using first variant; falls back to product page if variant unknown
      html += '<tr class="vs-cta-row"><td></td>';
      this.items.forEach((item) => {
        if (item.variant_id) {
          html += `<td>
            <div class="vs-cta-cell">
              <button class="vs-add-cart-btn" data-variant="${this.escAttr(String(item.variant_id))}" data-url="${this.safeUrl(item.url)}">หยิบใส่ตะกร้า</button>
              <a class="vs-view-btn" href="${this.safeUrl(item.url)}">ดูสินค้า</a>
            </div>
          </td>`;
        } else {
          html += `<td>
            <div class="vs-cta-cell">
              <a class="vs-view-btn vs-view-btn-primary" href="${this.safeUrl(item.url)}">หยิบใส่ตะกร้า</a>
            </div>
          </td>`;
        }
      });
      html += "</tr>";

      html += "</tbody></table>";
      body.innerHTML = html;

      // Highlight rows where values differ
      body
        .querySelectorAll(".vs-compare-table tbody tr:not(.vs-cta-row)")
        .forEach((row) => {
          const cells = [...row.querySelectorAll("td")].slice(1);
          if (cells.length < 2) return;
          const texts = cells.map((td) => td.textContent.trim());
          if (!texts.every((t) => t === texts[0])) {
            row.classList.add("vs-diff-row");
          }
        });

      // Bind remove buttons inside drawer
      body.querySelectorAll(".vs-product-remove").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.removeItem(btn.dataset.handle);
          if (this.items.length < 2) {
            this.closeDrawer();
          } else {
            this.renderCompareTable();
          }
        });
      });

      // Bind add to cart buttons
      body.querySelectorAll(".vs-add-cart-btn").forEach((btn) => {
        btn.addEventListener("click", () =>
          this.addToCart(btn.dataset.variant, btn.dataset.url),
        );
      });
    }

    async addToCart(variantId, fallbackUrl) {
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: parseInt(variantId), quantity: 1 }),
        });
        if (!res.ok) throw new Error();
        this.showToast("เพิ่มลงตะกร้าแล้ว ✓", "success");
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } catch {
        // Variant unavailable / sold out — send user to product page so they can pick another option
        if (fallbackUrl && fallbackUrl !== "#") {
          window.location.href = fallbackUrl;
        } else {
          this.showToast(
            "ไม่สามารถเพิ่มลงตะกร้าได้ กรุณาเลือกตัวเลือกที่หน้าสินค้า",
          );
        }
      }
    }

    // ── Toast ──
    showToast(msg) {
      let toast = document.getElementById("vs-compare-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.id = "vs-compare-toast";
        Object.assign(toast.style, {
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#1a1a1a",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: "8px",
          fontSize: "13px",
          fontWeight: "500",
          zIndex: "10000",
          transition: "opacity 0.3s",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        });
        document.body.appendChild(toast);
      }
      toast.textContent = msg;
      toast.style.opacity = "1";
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.style.opacity = "0";
      }, 2500);
    }

    // ── Metafield rendering — handles list.* (JSON arrays) and multi-line text ──
    renderMetafield(raw) {
      const s = String(raw).trim();
      if (!s) return "—";
      // Try JSON array (list.single_line_text_field, list.metaobject_reference, etc.)
      if (s.startsWith("[")) {
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr) && arr.length) {
            return (
              '<ul class="vs-mf-list">' +
              arr.map((v) => `<li>${this.esc(String(v))}</li>`).join("") +
              "</ul>"
            );
          }
        } catch {
          /* fall through */
        }
      }
      // Multi-line text — preserve line breaks
      if (s.includes("\n")) {
        return s
          .split("\n")
          .filter(Boolean)
          .map((line) => this.esc(line))
          .join("<br>");
      }
      return this.esc(s);
    }

    // ── Util ──
    esc(str) {
      if (!str) return "";
      const d = document.createElement("div");
      d.textContent = str;
      return d.innerHTML;
    }

    safeUrl(url) {
      if (!url) return "#";
      const s = String(url).trim();
      // Only allow relative paths and http(s) URLs — block javascript: etc.
      if (
        s.startsWith("/") ||
        s.startsWith("https://") ||
        s.startsWith("http://")
      ) {
        return this.escAttr(s);
      }
      return "#";
    }

    escAttr(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }
  }

  // ── Init ──
  function boot() {
    const root =
      document.getElementById("vs-compare-root") ||
      document.getElementById("vs-compare");
    if (root) new CompareWidget(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

